import { useState, type FormEvent, type ReactNode } from 'react';
import { TuvSudLogo } from './ui/TuvSudLogo';

// SHA-256 hash of the access password (plaintext is intentionally NOT in the code).
const PASSWORD_HASH = 'fc59bba23afd97170d053b558a3e1cabb493e042506d05a22aef207cb2072017';
const UNLOCK_KEY = 'tuvsud-prod-unlocked';

const NAVY = '#0b253b';
const BLUE = '#0046ad';

async function sha256Hex(input: string): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function alreadyUnlocked(): boolean {
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Lightweight client-side access gate. It requires a shared password before the
 * site renders — on every host, with no exceptions. This is a soft gate to keep
 * casual visitors out — not real security (the bundle is still delivered to the
 * browser).
 */
export function PasswordGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(() => alreadyUnlocked());
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  if (unlocked) return <>{children}</>;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setChecking(true);
    setError(false);
    const hash = await sha256Hex(value);
    if (hash === PASSWORD_HASH) {
      try {
        sessionStorage.setItem(UNLOCK_KEY, '1');
      } catch {
        /* sessionStorage unavailable — unlock for this render anyway */
      }
      setUnlocked(true);
    } else {
      setError(true);
      setChecking(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'radial-gradient(circle at top, rgba(0,70,173,0.12), transparent 40%), #f5f8fc',
        fontFamily: "'TUV SUD Pro', Arial, sans-serif",
        color: NAVY,
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 16,
          padding: '40px 32px',
          boxShadow: '0 24px 60px rgba(11,37,59,0.12)',
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <TuvSudLogo width={88} />
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Geschützter Bereich</h1>
        <p style={{ fontSize: 14, color: 'rgba(11,37,59,0.7)', margin: '0 0 24px', lineHeight: 1.5 }}>
          Bitte gib das Passwort ein, um fortzufahren.
        </p>

        <input
          type="password"
          value={value}
          autoFocus
          aria-label="Passwort"
          placeholder="Passwort"
          onChange={(e) => {
            setValue(e.target.value);
            setError(false);
          }}
          style={{
            width: '100%',
            height: 48,
            padding: '0 16px',
            fontSize: 16,
            border: `1px solid ${error ? '#e11d48' : 'rgba(11,37,59,0.2)'}`,
            borderRadius: 8,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {error ? (
          <p style={{ color: '#e11d48', fontSize: 13, margin: '10px 0 0' }}>Falsches Passwort.</p>
        ) : null}

        <button
          type="submit"
          disabled={checking || value.length === 0}
          style={{
            width: '100%',
            height: 48,
            marginTop: 20,
            background: BLUE,
            color: '#fff',
            border: 0,
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 600,
            cursor: checking || value.length === 0 ? 'not-allowed' : 'pointer',
            opacity: checking || value.length === 0 ? 0.6 : 1,
          }}
        >
          {checking ? 'Prüfe…' : 'Weiter'}
        </button>
      </form>
    </div>
  );
}
